import { DataTableV2, Title } from "components/commons";
import React, { useCallback, useMemo, useState } from "react";
import locale from "localization";
import styles from "../cash-noncash.module.scss";
import useDataTable from "hooks/useDataTable";
import { isArray, sortBy } from "lodash";
import { nonCashSummaryColumn } from "./noncash-summary-column";
import { nonCashSummaryMapper } from "./noncash-summary.mapper";
import { PaymentMethod } from "enums";
import PaymentMethodTransactionModal from "./payment-method-transaction-modal/payment-method-transaction-modal";
import { useModal } from "hooks";
import { prettifyPaymentMethod } from "utils/pretty.utils";
import { initialShiftRecordFilterState } from "modules/records/shift-records/shift-records-filter.state";

export default function ShiftDetailsNonCashSummary({ shift, refetchShift, pageMode }) {
  const paymentMethodTransactionModal = useModal();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const displayedMethods = useMemo(() => Object.values(PaymentMethod).map((value) => value), []);

  const transactionsItems = useMemo(() => {
    const result = [];
    Object.values(shift.transactionItem).forEach((productTransaction) => {
      if (isArray(productTransaction)) {
        result.push(...productTransaction.map((t) => t.transaction));
      }
    });

    return result;
  }, [shift.transactionItem]);

  const transactionsItemDetails = useMemo(() => {
    const result = [];
    Object.values(shift.transactionItem).forEach((productTransaction) => {
      if (isArray(productTransaction)) {
        result.push(...productTransaction.map((t) => t));
      }
    });

    return result;
  }, [shift.transactionItem]);

  const mapPaymentTransaction = useCallback((t) => {
    // const transactionPaymentLength =
    //   saleTransactionsDetailed.filter((sale) => sale.transactionId === t.transactionId).length ||
    //   1;
    // const excessShare = t.excessAmount / transactionPaymentLength;
    // const totalAmount = t.totalAmount - t.excessAmount;
    return { ...t, discount: t.discount + t.vatAmount };
  }, []);

  const sorted = useMemo(() => {
    const transactions = sortBy(
      shift?.transactionPaymentMethod.nonCash,
      "transactionItemId",
    ).reverse();

    // transactionCount,
    // method,
    // excessAmount,
    // // transactions: paymentMethodsTransactions,
    // discount,
    // // amountPaid,
    // totalAmount,
    // finalAmount,

    const groupedPayments = {};
    transactions.forEach((payment) => {
      groupedPayments[payment.method] = [...(groupedPayments[payment.method] || []), payment];
    });

    const result = [];
    Object.entries(groupedPayments).forEach(([key, value]) => {
      let resultTotalAmount = 0;
      let resultFinalAmount = 0;
      let resultExcessAmount = 0;
      let resultDiscount = 0;
      value.forEach((val) => {
        const { totalAmount, finalAmount, excessAmount, discount } = mapPaymentTransaction(val);

        resultTotalAmount += totalAmount;
        resultFinalAmount += finalAmount;
        resultExcessAmount += excessAmount;
        resultDiscount += discount;
      });

      result.push({
        transactionCount: [...new Set(value.map((t) => t.transactionId))].length,
        method: key,
        totalAmount: resultTotalAmount,
        finalAmount: resultFinalAmount,
        excessAmount: resultExcessAmount,
        discount: resultDiscount,
      });
    });

    const filteredDisplayed = result
      .filter((payment) => displayedMethods.includes(payment.method))
      .sort((a, b) =>
        prettifyPaymentMethod(a.method).localeCompare(prettifyPaymentMethod(b.method)),
      );

    return filteredDisplayed;
  }, [displayedMethods, mapPaymentTransaction, shift?.transactionPaymentMethod.nonCash]);

  const nonCashTransactions = useMemo(() => {
    return sortBy(shift?.transactionPaymentMethod.nonCash, "transactionItemId").reverse();
  }, [shift?.transactionPaymentMethod.nonCash]);

  const paymentMethodTransactions = useMemo(() => {
    const transactions = sortBy(
      shift?.transactionPaymentMethod.nonCash,
      "transactionItemId",
    ).reverse();

    let filtered;
    if (selectedPaymentMethod === PaymentMethod.Other) {
      filtered = transactions.filter((payment) => !displayedMethods.includes(payment.method));
    } else {
      filtered = transactions.filter((transaction) => transaction.method === selectedPaymentMethod);
    }

    filtered = filtered.map((t) => {
      return mapPaymentTransaction(t);
    });

    return filtered;
  }, [
    displayedMethods,
    mapPaymentTransaction,
    selectedPaymentMethod,
    shift?.transactionPaymentMethod.nonCash,
  ]);

  const onViewPaymentMethodTransaction = useCallback(
    (method) => {
      paymentMethodTransactionModal.show({});
      setSelectedPaymentMethod(method);
    },
    [paymentMethodTransactionModal],
  );

  const { table } = useDataTable({
    value: sorted || [],
    filter: { initialState: initialShiftRecordFilterState(), mapper: (data) => data },
    table: {
      key: "nonCash",
      name: "nonCashTransactions",
      mapper: (val) =>
        nonCashSummaryMapper({
          val,
          onViewPaymentMethodTransaction,
          nonCashTransactions: nonCashTransactions,
          transactionsItemDetails,
        }),
      columns: nonCashSummaryColumn,
    },
  });

  return (
    <>
      <Title small black>
        {locale.nonCashSummary}
      </Title>
      <div className={styles.spacer10} />
      <div className={styles.tableContainer}>
        <DataTableV2
          {...table}
          hideNoSearchResultRender
          tableVersion="v2"
          hidePagination
          size="small"
        />
      </div>
      <div className={styles.spacer50} />
      <PaymentMethodTransactionModal
        method={selectedPaymentMethod}
        paymentMethodTransactions={paymentMethodTransactions}
        allTransactions={transactionsItems}
        nonCashTransactions={nonCashTransactions}
        transactionsItemDetails={transactionsItemDetails}
        onRefetch={refetchShift}
        pageMode={pageMode}
        shift={shift}
        {...paymentMethodTransactionModal}
      />
    </>
  );
}
